import React, { useEffect, useState } from 'react';
import Meta from 'src/components/Meta';
import { useGaEventPageView } from 'src/utils/gaEvent';
import { Divider, PageContainer, PageControlSkeleton, Poppins, Spacer } from 'src/common';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';
import { useQuery } from 'react-query';
import { ConfirmDeleteModal, IncidentModal } from 'src/components/modals';
import { QKeys } from 'src/api/types';
import MetaSheet from './comps/MetaSheet';
import { getIncident } from 'src/api/incidents';
import { omit } from 'lodash';
import useActionsApp from 'src/redux/actionHooks/useActionsApp';
import useVersion from 'src/hooks/useVersion';
import { AuditSection, LinkedScenariosSection } from './sections';
import { ObjectKind, useDeleteIncidentMutation, useIncidentQuery } from 'src/graphql/_generated';
import { isQueryLoading, useCache } from 'src/graphql';
import { useApolloClient } from '@apollo/client';
import { showSuccessToast, toastMessages } from 'src/components/Toast';
import ActionsSection from 'src/components/ActionsSection';

const IncidentPage = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { setLocationPaths } = useActionsApp();
  const { versionId } = useVersion();
  const { refetchQuery } = useCache();
  const client = useApolloClient();

  const navigate = useNavigate();

  const {
    data: { incident } = {},
    loading,
    networkStatus,
    error,
  } = useIncidentQuery({
    variables: {
      id: incidentId!,
      versionId,
    },
  });
  const isLoading = isQueryLoading({ data: incident, loading, networkStatus });

  useQuery([QKeys.Incident, incidentId, { version: versionId }], () => getIncident(incidentId as string, { version: versionId }));

  useEffect(() => {
    if (incident) {
      setLocationPaths({ incident: { id: incident.id, name: incident.name } });
    }

    return () => {
      setLocationPaths({ incident: undefined });
    };
  }, [incident]);

  useGaEventPageView('Cydea | Security Incidents | Incident');

  const [remove] = useDeleteIncidentMutation({
    variables: { id: incidentId! },
  });

  return (
    <>
      <Meta title={`Cydea | Security Incidents | Incident`} feedbackScript />

      {isDeleteModalOpen && (
        <ConfirmDeleteModal
          onConfirm={async () => {
            await remove();
            client.cache.evict({ id: client.cache.identify({ __typename: 'Incident', id: incidentId }) });
            client.cache.gc();
            refetchQuery({ include: ['incidents'] });
            showSuccessToast(toastMessages.incident.deleted);
            navigate('/incidents');
          }}
          onClose={() => setIsDeleteModalOpen(false)}
        />
      )}
      {isEditModalOpen && (
        <IncidentModal
          initialData={omit(incident, ['__typename'])}
          onClose={() => setIsEditModalOpen(false)}
          invalidateQueries={['incident']}
        />
      )}

      <PageContainer data-cy="incident-page">
        {isLoading && <PageControlSkeleton />}
        {incident && (
          <MetaSheet
            data={incident}
            editable={{
              onDeleteSelect: () => setIsDeleteModalOpen(true),
              onEditSelect: () => setIsEditModalOpen(true),
            }}
          >
            <Spacer $px={30} />
            <Divider />
            <LinkedScenariosSection version={versionId} incidentName={incident.name} />
            <Divider />
            <ActionsSection objectType={ObjectKind.Incident} objectId={incident.id} name={incident.name} />
            <Divider />
            <AuditSection version={versionId} />
          </MetaSheet>
        )}
        {error && <Poppins color="error">{error.message}</Poppins>}
      </PageContainer>
    </>
  );
};

export default IncidentPage;
